import React from "react";
import PropTypes from "prop-types";
import { useFormikContext, Field, ErrorMessage, getIn } from "formik";
import classNames from "classnames";

import { states } from "../states";
import { getValueFromStringObjPath } from "../webform-elements";
import DashedField from "./dashed-field";

const TextField = ({ item, compositeIndex, compositeParent }) => {
  const { values, handleChange, handleBlur, errors, touched } =
    useFormikContext();

  const { invisible, visible, enabled, disabled, optional, required } = states(
    item["#states"],
    values
  );

  const value = compositeParent
    ? typeof compositeIndex !== "undefined"
      ? getValueFromStringObjPath(values, compositeParent)?.[compositeIndex]?.[
          item.id
        ]
      : getValueFromStringObjPath(values, compositeParent)[item.id]
    : values[item.id];
  const identifier = compositeParent
    ? typeof compositeIndex !== "undefined"
      ? `${compositeParent}[${compositeIndex}][${item.id}]`
      : `${compositeParent}[${item.id}]`
    : item.id;
  const fieldError = getIn(errors, identifier);
  const fieldTouched = getIn(touched, identifier);

  return (
    <div
      className={classNames({
        "form-group": true,
        "text-field": true,
        hidden: invisible || !visible,
      })}
      style={item["#flex"] ? { flex: item["#flex"] } : {}}
    >
      <label htmlFor={identifier} className="form-label">
        {item["#title"]}
        {(!!item["#required"] || required) && !optional && visible && (
          <span className="required">*</span>
        )}
      </label>

      <DashedField>
        <Field
          id={identifier}
          name={identifier}
          type="text"
          required={(!!item["#required"] || required) && !optional && visible}
          disabled={!enabled || disabled || item["#disabled"]}
          className={classNames({
            "form-control": true,
            "is-invalid": fieldError && fieldTouched,
          })}
          placeholder={item["#placeholder"] != null ? item["#placeholder"] : ""}
          onChange={handleChange}
          onBlur={handleBlur}
          value={value}
          maxLength={item["#maxlength"] ? item["#maxlength"] : null}
          minLength={item["#minlength"] ? item["#minlength"] : null}
        />
      </DashedField>

      <ErrorMessage
        role="region"
        aria-live="polite"
        component="span"
        name={identifier}
        className="error-message"
      />
      {!!item["#description"] && (
        <small
          className="form-description text-muted form-text"
          dangerouslySetInnerHTML={{ __html: item["#description"] }}
        />
      )}
    </div>
  );
};

TextField.propTypes = {
  item: PropTypes.shape({
    "#disabled": PropTypes.bool,
    "#default_value": PropTypes.string,
    "#description": PropTypes.string,
    "#flex": PropTypes.number,
    "#maxlength": PropTypes.number,
    "#minlength": PropTypes.number,
    "#placeholder": PropTypes.string,
    "#required": PropTypes.bool,
    "#required_error": PropTypes.string,
    "#states": PropTypes.object,
    "#submit__label": PropTypes.string,
    "#title": PropTypes.string,
    "#type": PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }),
  compositeIndex: PropTypes.number,
  compositeParent: PropTypes.string,
};

export default TextField;
